import cuid from "cuid";
import dayjs from "dayjs";
import { defaults, get, isString, keyBy, map, set } from "lodash";
import moment from "moment";
import omitDeep from "omit-deep-lodash";
import { useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { GENERATE_QUOTE_DOCUMENTS } from "utils/api/graphql/mutations/attachment";
import { BUILD_GENERAL_COST } from "utils/api/graphql/mutations/costs";
import {
  CASE_TYPES,
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  KEYS,
  SUBSCRIPTION_STATUSES,
} from "utils/constants";
import Template from "../Template";
import View from "./View";

const EffectiveDate = ({
  tracer,
  project,
  onNext,
  currentStatus,
  onBack,
  updateUrl,
}) => {
  const subscriptionId =
    get(project, "fields.currentSubscription") ||
    localStorage.getItem(KEYS.SUBSCRIPTION_ID);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const insurancePlans = useMemo(
    () =>
      get(project, "fields.insurancePlans", [])?.map((id) => ({
        AND: [
          {
            insurancePlans: { id },
          },
          { project: { id: get(project, "id") } },
        ],
      })),
    [project],
  );
  const [updateProject] = useMutation(graphql.mutations.UPDATE_PROJECT);
  const [getSubscription, { data }] = useLazyQuery(
    graphql.queries.SUBSCRIPTION,
    { fetchPolicy: "no-cache" },
  );

  const attachmentsFilter = {
    where: {
      OR: [
        {
          project: {
            id: get(project, "id"),
          },
        },
        ...insurancePlans,
      ],
    },
  };

  const [updateSubscription] = useMutation(
    graphql.mutations.UPDATE_SUBSCRIPTION,
    {
      refetchQueries: [
        {
          query: graphql.queries.SUBSCRIPTION,
          awaitRefetchQueries: true,
          variables: { where: { id: subscriptionId } },
        },
      ],
    },
  );

  const [generateQuoteDocuments] = useMutation(GENERATE_QUOTE_DOCUMENTS, {
    refetchQueries: [
      {
        query: graphql.queries.ATTACHMENTS,
        awaitRefetchQueries: true,
        variables: attachmentsFilter,
      },
    ],
  });

  const [buildGeneralCost] = useMutation(BUILD_GENERAL_COST, {
    variables: {
      data: {
        project: { id: project.id },
        insurancePlan: {
          id: get(data, "subscription.insurancePlan.id"),
        },
      },
    },
  });

  const [getGeneralCost, { data: generalCost, refetch }] = useLazyQuery(
    graphql.queries.GENERAL_COST,
    {
      fetchPolicy: "no-cache",
      variables: {
        where: {
          project: { id: get(project, "id") },
          insurancePlan: {
            id: get(data, "subscription.insurancePlan.id"),
          },
        },
      },
      onCompleted: () => {
        setLoading(false);
      },
    },
  );

  useEffect(() => {
    if (subscriptionId)
      getSubscription({
        variables: { where: { id: subscriptionId } },
        onCompleted: () => {
          getGeneralCost();
        },
      });
  }, []);

  const recalculateCost = ({
    issuanceDate,
    oldIssuanceDate,
    setFieldValue,
    setShowChangeCostModal,
    isCancel,
    isRia,
  }) => {
    const cost = get(generalCost, "generalCost.cost");
    const formattedIssuanceDate = isString(issuanceDate)
      ? moment(issuanceDate)
      : issuanceDate;

    const formattedOldIssuanceDate = isString(oldIssuanceDate)
      ? moment(oldIssuanceDate)
      : oldIssuanceDate;

    if (formattedIssuanceDate.isSame(formattedOldIssuanceDate.format(), "day"))
      return;

    updateProject({
      variables: {
        where: { id: project.id },
        data: {
          contract: {
            id: get(project, "contract.id"),
            issuanceDate,
          },
        },
      },
      onCompleted: () => {
        buildGeneralCost({
          onCompleted: ({ buildGeneralCost: generalCost }) => {
            if (!generalCost?.cost) {
              setFieldValue("project.contract.issuanceDate", oldIssuanceDate);
              setShowChangeCostModal({
                noCost: true,
              });
              return updateProject({
                variables: {
                  where: { id: project.id },
                  data: {
                    contract: {
                      id: get(project, "contract.id"),
                      issuanceDate: oldIssuanceDate,
                    },
                  },
                },
                onCompleted: () => {
                  buildGeneralCost();
                },
              });
            }
            if (cost === generalCost.cost) return;
            if (isCancel) {
              setFieldValue("project.contract.issuanceDate", issuanceDate);
              refetch();
              return setShowChangeCostModal({});
            }
            setShowChangeCostModal({
              isRia,
              issuanceDate: {
                old: formattedOldIssuanceDate.format(DATE_FORMAT),
                new: formattedIssuanceDate.format(DATE_FORMAT),
              },
              cost: {
                old: cost,
                new: generalCost.cost,
              },
            });
            refetch();
          },
        });
      },
    });
  };

  const onSubmit = ({ project: projectValues, telecoms }) => {
    setSubmitting(true);

    const mappedTelecoms = map(telecoms, (item, key) =>
      defaults(item, { id: cuid(), system: key }),
    );
    const isEffectiveDateChanged = !dayjs(
      get(projectValues, "contract.issuanceDate"),
    ).isSame(dayjs(get(project, "contract.issuanceDate")));
    const { insurancePlan, ...subscription } = omitDeep(
      get(data, "subscription"),
      "__typename",
    );
    set(subscription, "project", {
      ...projectValues,
      terminationRequest: {
        id: cuid(),
        ...projectValues.terminationRequest,
      },
    });

    set(
      subscription,
      "project.fields.replaceContract",
      get(projectValues, "fields.replaceContract"),
    );
    set(
      subscription,
      "project.fields.oldContractYear",
      get(projectValues, "fields.oldContractYear"),
    );

    set(
      subscription,
      "project.contract.caseType",
      get(subscription, "project.ria")
        ? CASE_TYPES.RETURN_TO_COMPETITION
        : CASE_TYPES.NEW_CONTRACT,
    );
    set(subscription, "status", SUBSCRIPTION_STATUSES.IN_PROGRESS);
    set(subscription, "project.contact.telecoms", mappedTelecoms);

    updateSubscription({
      variables: {
        where: {
          id: subscriptionId,
        },
        data: subscription,
      },
      onCompleted: (_) => {
        const source = localStorage.getItem(KEYS.SOURCE);
        if (isEffectiveDateChanged)
          generateQuoteDocuments({
            variables: {
              data: {
                project: { id: get(project, "id") },
                insurancePlans: [{ id: get(insurancePlan, "id") }],
              },
            },
          });
        updateUrl({
          subscriptionId,
          url: get(
            insurancePlan,
            "config.generatedUrl",
            `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
          ),
        });

        const hasRiaData =
          get(subscription, "project.ria") ||
          get(subscription, "project.fields.replaceContract") ||
          get(subscription, "project.terminationRequest");
        // const delayTime = hasRiaData ? 800 : 300;

        setSubmitting(false);
        onNext({
          payload: {
            contact: get(subscription, "project.contact"),
          },
        });
      },
      onError: () => {
        setSubmitting(false);
      },
    });
  };

  const telecoms = defaults(keyBy(get(project, "contact.telecoms"), "system"), {
    [CONTACT_POINT_SYSTEM.BIRTH_CITY]: { value: null },
    [CONTACT_POINT_SYSTEM.BIRTH_COUNTRY]: { value: "France" },
    [CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH]: { value: null },
  });
  const initialValues = {
    resiliation: false,
    telecoms,
    project: omitDeep(project, "__typename"),
  };

  return (
    <Template
      progress={currentStatus.progress}
      onBack={onBack}
      loading={loading}
      contact={get(project, "contact")}
      project={project}
      HeaderComponent={{
        subscription: get(data, "subscription"),
        tracer,
        project,
        contact: get(project, "contact"),
      }}
      View={
        <View
          initialValues={initialValues}
          locked={get(data, "subscription.locked")}
          subscription={get(data, "subscription")}
          recalculateCost={recalculateCost}
          onNext={onSubmit}
          onSubmitModal={onNext}
          project={get(project, "contact")}
          onBack={onBack}
          submitting={submitting}
        />
      }
    />
  );
};
export default EffectiveDate;
